import getWebSlugsAndConfig from "~/utils/getWebSlugsAndConfig";
import getGlobalFilterQueryIdentifier from "~/utils/getGlobalFilterQueryIdentifier";
import formatDatabaseDateOnly from "~/utils/formatDatabaseDateOnly";
import Vueset from "~/utils/Vueset";

const emptyFilter = () => ({
    "taskProgress": null,
    "include_all_participants": false,
    "userStatus": [],
    "userRole": [],
    "signed_up_start_at": null,
    "signed_up_end_at": null,
    "progress_start_at": null,
    "progress_end_at": null,
    "profile_groups_condition": null,
    "queryIdentifier": null,
    "profile_groups": ""
});
export const state = () => ({
    globalFilters: [],
});

export const getters = {
    currentFilter: (state) => (context) => {
        let identifier = getGlobalFilterQueryIdentifier.bind(context)();
        return state.globalFilters.find(filter => filter.queryIdentifier === identifier);
    },
    currentFilterQuery: (state) => (context) => {
        const {taskSlug} = getWebSlugsAndConfig.bind(context)();
        const isTaskLevel = !!taskSlug;

        const {user} = context.$route.params;
        let identifier = getGlobalFilterQueryIdentifier.bind(context)();

        const filter = state.globalFilters.find(filter => filter.queryIdentifier === identifier);

        const query = {};
        query.queryIdentifier = identifier;
        if(filter){
            query.userStatus = filter.userStatus.join(',');
            query.userRole = filter.userRole.join(',');
            query.signed_up_start_at = filter.signed_up_start_at;
            query.signed_up_end_at = filter.signed_up_end_at;
            query.profile_groups = filter.profile_groups;
            query.profile_groups_condition = filter.profile_groups_condition;

            if(isTaskLevel || user){
                query.taskProgress = filter.taskProgress;
                query.progress_start_at = filter.progress_start_at;
                query.progress_end_at = filter.progress_end_at;

                if (filter.include_all_participants) {
                    query.include_all_participants = true;
                }
            }
        }

        return filter ? query : null;
    }
};

export const mutations = {
    UPDATE_GLOBAL_FILTER(state, payload){
        const filterExistsIndex = state.globalFilters.findIndex(filter => filter.queryIdentifier === payload.queryIdentifier);
        if(filterExistsIndex > -1){
            Vueset(state.globalFilters, filterExistsIndex, payload);
        }else{
            state.globalFilters.push(payload);
        }
    },
    CLEAR_GLOBAL_FILTER(state, queryIdentifier){
        const filterLocation = state.globalFilters.findIndex(filter => filter.queryIdentifier === queryIdentifier);
        if(filterLocation > -1){
            state.globalFilters.splice(filterLocation ,1);
        }
    },
    ADD_GLOBAL_FILTER(state, payload){
        const filter = {
            ...emptyFilter(),
            ...payload
        };
        const index = state.globalFilters.findIndex(item => item.queryIdentifier === payload.queryIdentifier);
        if(index > -1){
            state.globalFilters[index] = filter;
        }else{
            state.globalFilters.push(filter);
        }

    }
};

const getProfileGroupsFromGroupIds = ({filterGroups, profileGroups}) => {
    const profileGroupsFiltered = filterGroups ? String(filterGroups).split(',') : [];

    if (!profileGroups || !profileGroups.length) {
        return profileGroupsFiltered.map((id) => ({
            id: parseInt(id, 10),
            name: `Profile group #${id}`,
            category: { name: 'Profile group' },
        }));
    }

    const categoryIndexes = profileGroupsFiltered.map(pgId => (profileGroups.findIndex(category => {
        return category.groups.some(group => group.id === parseInt(pgId));
    })));
    const groups = categoryIndexes.map((cat, index) => {
        if (cat < 0) {
            return {
                id: parseInt(profileGroupsFiltered[index], 10),
                name: `Profile group #${profileGroupsFiltered[index]}`,
                category: { name: 'Profile group' },
            };
        }

        const group = profileGroups[cat].groups.find(
            (profileGroup) => profileGroup.id === parseInt(profileGroupsFiltered[index], 10)
        );

        if (!group) {
            return null;
        }

        return {
            ...group,
            category: group.category || { name: profileGroups[cat].name },
        };
    });
    return groups.filter(group => group);
};
const makeProfileGroupsReadable = ({profile_groups, filter}) => {
    const profile_groups_readable = profile_groups.map(pg => (
        `${pg.category.name}: ${pg.name}`
    ));

    if(!profile_groups_readable.length){
        return '';
    }

    return profile_groups_readable.length === 1
        ? `<b>Profile group:</b> ${profile_groups_readable.join(', ')}`
        : `<b>${filter.profile_groups_condition === 'OR' ? 'Any' : 'All'} of the profile groups:</b> ${profile_groups_readable.join(', ')}`;
};
const getSignedUpReadable = ({filter}) => {
    const start = filter.signed_up_start_at ? formatDatabaseDateOnly(filter.signed_up_start_at) : null;
    const end = filter.signed_up_end_at ? formatDatabaseDateOnly(filter.signed_up_end_at) : null;

    if(start && !end){
        return `<b>Users signed up since:</b> ${start}`;
    }
    if(!start && end){
        return `<b>Users signed up before:</b> ${end}`;
    }
    if(!start && !end){
        return '';
    }

    return `<b>Users signed up between:</b> ${start} - ${end}`;
};
const getTaskProgressReadable = ({filter}) => {
    const start = filter.progress_start_at ? formatDatabaseDateOnly(filter.progress_start_at) : null;
    const end = filter.progress_end_at ? formatDatabaseDateOnly(filter.progress_end_at) : null;

    if(start && !end){
        return `<b>Users started task after:</b> ${start}`;
    }
    if(!start && end){
        return `<b>Users finished task before:</b> ${end}`;
    }
    if(!start && !end){
        return '';
    }

    return `<b>Users finished task between:</b> ${start} - ${end}`;
};
const getTaskProgressStatus = ({filter, statuses}) => {
    if (filter.include_all_participants) {
        return `<b>Progress status:</b> All participants`;
    }

    const progressValues = Array.isArray(filter.taskProgress)
        ? filter.taskProgress
        : (filter.taskProgress ? [filter.taskProgress] : []);

    let taskStatuses = progressValues.map(status => {
        return statuses.task.find(task => task.value === status);
    }) || [];

    taskStatuses = taskStatuses.filter(s => s);

    return taskStatuses.length
        ? `<b>Progress status:</b> ${taskStatuses.map(s => s.name).join(',')}`
        : ``;
};
const getUserStatus = ({filter, statuses}) => {
    let userStatuses = filter.userStatus?.map(status => {
        return statuses.default.find(task => task.value === status);
    }) || [];

    userStatuses = userStatuses.filter(s => s);

    return userStatuses.length
        ? `<b>User status:</b> ${userStatuses.map(s => s.name).join(',')}`
        : ``;
};
const getUserRole = ({filter, roles}) => {
    let userRoles = filter.userRole?.map(status => {
        return roles.find(task => task.value === status);
    }) || [];

    userRoles = userRoles.filter(s => s);

    return userRoles.length
        ? `<b>User roles:</b> ${userRoles.map(s => s.name).join(',')}`
        : ``;
};

export const filterToReadable = ({filter, profileGroups, statuses, roles}) => {

    const profile_groups = getProfileGroupsFromGroupIds({filterGroups: filter.profile_groups, profileGroups});
    const profile_groups_readable = makeProfileGroupsReadable({profile_groups, filter});
    const signed_up_readable = getSignedUpReadable({filter});
    const task_progress_readable = getTaskProgressReadable({filter});
    const task_progress_status_readable = getTaskProgressStatus({filter, statuses});
    const user_status_readable = getUserStatus({filter, statuses});
    const user_role_readable = getUserRole({filter, roles});

    const keys = [
        'profile_groups_readable',
        'signed_up_readable',
        'task_progress_readable',
        'task_progress_status_readable',
        'user_status_readable',
        'user_role_readable',
    ];

    const readable = {
        profile_groups,
        profile_groups_readable,
        signed_up_readable,
        task_progress_readable,
        task_progress_status_readable,
        user_status_readable,
        user_role_readable,
    };

    let response = '';
    Object.keys(keys).forEach((value, index) => {
        const message = readable[keys[index]];
        response += message ? `<li>${message}</li>` : '';
    });

    readable.response = `<br/><br/>You have filtered the results to include:<ul>${response}</ul>`;
    readable.trimmedResponse = `<ul>${response}</ul>`;

    return readable;
};

/**
 * Compact chip summaries for AI chat / filter badges.
 * @returns {{chips: Array<{key: string, label: string, tone?: string}>, tooltip: string, count: number}}
 */
export const filterToChips = ({filter, profileGroups, statuses, roles}) => {
    const chips = [];
    const profileGroupsResolved = getProfileGroupsFromGroupIds({
        filterGroups: filter.profile_groups,
        profileGroups,
    });

    if (profileGroupsResolved.length) {
        const condition = filter.profile_groups_condition === 'OR' ? 'OR' : 'AND';
        const conditionLabel = condition === 'OR' ? 'Any of' : 'All of';

        if (profileGroupsResolved.length > 1) {
            chips.push({
                key: 'profile_groups_condition',
                label: conditionLabel,
                tone: 'condition',
            });
        }

        profileGroupsResolved.forEach((group) => {
            chips.push({
                key: `profile_group_${group.id}`,
                label: `${group.category?.name || 'Group'}: ${group.name}`,
                tone: 'profile',
            });
        });
    }

    const signedUp = getSignedUpReadable({filter});
    if (signedUp) {
        chips.push({
            key: 'signed_up',
            label: signedUp.replace(/<\/?b>/g, ''),
            tone: 'meta',
        });
    }

    const taskProgressDates = getTaskProgressReadable({filter});
    if (taskProgressDates) {
        chips.push({
            key: 'task_progress_dates',
            label: taskProgressDates.replace(/<\/?b>/g, ''),
            tone: 'meta',
        });
    }

    const taskProgressStatus = getTaskProgressStatus({filter, statuses});
    if (taskProgressStatus) {
        chips.push({
            key: 'task_progress',
            label: taskProgressStatus.replace(/<\/?b>/g, ''),
            tone: 'meta',
        });
    }

    const userStatus = getUserStatus({filter, statuses});
    if (userStatus) {
        chips.push({
            key: 'user_status',
            label: userStatus.replace(/<\/?b>/g, ''),
            tone: 'meta',
        });
    }

    const userRole = getUserRole({filter, roles});
    if (userRole) {
        chips.push({
            key: 'user_role',
            label: userRole.replace(/<\/?b>/g, ''),
            tone: 'meta',
        });
    }

    const tooltipParts = [];
    if (profileGroupsResolved.length) {
        const condition = filter.profile_groups_condition === 'OR' ? 'Any of' : 'All of';
        const groupLabels = profileGroupsResolved.map((group) => `${group.category?.name || 'Group'}: ${group.name}`);
        tooltipParts.push(
            profileGroupsResolved.length > 1
                ? `Profile groups (${condition}): ${groupLabels.join(', ')}`
                : `Profile group: ${groupLabels[0]}`
        );
    }

    chips
        .filter((chip) => chip.tone !== 'condition' && chip.tone !== 'profile')
        .forEach((chip) => tooltipParts.push(chip.label));

    return {
        chips,
        tooltip: tooltipParts.join('\n'),
        count: chips.filter((chip) => chip.tone !== 'condition').length,
    };
};

export const filterProfileGroupToReadable = ({filter, profileGroups}) => {

    const profile_groups = getProfileGroupsFromGroupIds({filterGroups: filter.profile_groups, profileGroups});
    const profile_groups_readable = makeProfileGroupsReadable({profile_groups, filter});

    const keys = [
        'profile_groups_readable'
    ];

    const readable = {
        profile_groups,
        profile_groups_readable,
    };

    let response = '';
    Object.keys(keys).forEach((value, index) => {
        const message = readable[keys[index]];
        response += message ? `<li>${message}</li>` : '';
    });

    readable.response = `You have filtered the results to include:<ul>${response}</ul>`;

    return readable;
};
